/* Product-scoped native bridge. The genuine app owns checkout and customer data.
 * Requests are GET /cart.js only; no cart, settings or order mutations. */
export function cartFingerprint(cart) {
  if (!cart || !Array.isArray(cart.items)) throw Error('Invalid public cart');
  const rows = new Map();
  for (const item of cart.items) {
    const id = String(item.variant_id ?? item.id ?? '');
    const quantity = Number(item.quantity);
    if (!/^\d+$/.test(id) || !Number.isSafeInteger(quantity) || quantity < 1) throw Error('Invalid public cart item');
    rows.set(id, (rows.get(id) || 0) + quantity);
  }
  return [...rows].sort(([a], [b]) => a.localeCompare(b)).map(([id, quantity]) => id + ':' + quantity).join('|');
}

export function restorationEvidence({baseline, duringOpen, intentAt, closedAt, clear, restoreAdd}) {
  if (!clear || !(intentAt > 0) || !(closedAt >= intentAt) || clear.startTime < intentAt || clear.responseEnd < closedAt) return false;
  if (Number.isFinite(clear.responseStatus) && clear.responseStatus > 0) return clear.responseStatus >= 200 && clear.responseStatus < 300;
  if (baseline) return Boolean(restoreAdd && restoreAdd.startTime >= clear.responseEnd && restoreAdd.responseEnd >= restoreAdd.startTime && (!restoreAdd.responseStatus || restoreAdd.responseStatus >= 200 && restoreAdd.responseStatus < 300));
  return Boolean(duringOpen && duringOpen.fingerprint && duringOpen.completedAt < intentAt);
}

const ROOT = '[data-olla-final][data-native-ready="true"]';
const OWNER = '#_rsi-buy-now-button-overwrite,#_rsi-buy-now-button,#rsi_buy_now_button,._rsi-buy-now-button-product';
const MODAL = '#_rsi-cod-form-modal._rsi-cod-form-modal-open';
const instances = new WeakMap();
const visible = node => Boolean(node?.isConnected && !node.closest('[hidden],[inert]') && node.getClientRects().length && getComputedStyle(node).display !== 'none' && getComputedStyle(node).visibility !== 'hidden');

function mount(root) {
  if (instances.has(root)) return;
  const abort = new AbortController(), owner = root.querySelector('[data-olla-native-owner]');
  if (!owner || !/^\d+$/.test(root.dataset.productId || '') || !/^\d+$/.test(root.dataset.variantId || '')) return;
  let state = 'initializing', baseline = null, duringOpen = null, pending = false, dispatching = false;
  let modalWasOpen = false, intentAt = 0, closedAt = 0, clear = null, restoreAdd = null;
  let epoch = 0, readRunning = false, deadline = 0, timer = 0, frame = 0, disposed = false;
  const reads = new Set();
  const ownedStyles = new Map();
  const status = root.querySelector('[data-olla-native-status]');
  const sticky = root.querySelector('[data-olla-sticky]');
  const footer = root.querySelector('.cu-olla-brand-footer');
  const bridges = [...root.querySelectorAll('[data-olla-scroll-native]')];
  const bridgeAccessibility = bridges.map(node => ({node, role: node.getAttribute('role'), popup: node.getAttribute('aria-haspopup')}));
  bridges.forEach(node => { node.setAttribute('role', 'button'); node.setAttribute('aria-haspopup', 'dialog'); });
  const now = () => performance.now();
  const modal = () => [...document.querySelectorAll(MODAL)].find(visible) || null;
  const setStatus = message => { if (status) { status.textContent = message; status.hidden = !message; } };
  function updateSticky(activeModal = modal()) {
    if (!sticky) return;
    const ownerRect = owner.getBoundingClientRect();
    const footerRect = footer?.getBoundingClientRect();
    const passedOwner = ownerRect.height > 0 && ownerRect.bottom <= 0;
    const footerVisible = footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0;
    sticky.hidden = !passedOwner || Boolean(activeModal) || Boolean(footerVisible) || state === 'reload-required';
  }
  function ownStyle(node, name, value) {
    if (!node) return;
    if (!ownedStyles.has(node)) ownedStyles.set(node, new Map());
    const properties = ownedStyles.get(node);
    if (!properties.has(name)) properties.set(name, {before: node.style.getPropertyValue(name), priority: node.style.getPropertyPriority(name), after: value});
    if (node.style.getPropertyValue(name) !== value || node.style.getPropertyPriority(name) !== 'important') node.style.setProperty(name, value, 'important');
    properties.get(name).after = node.style.getPropertyValue(name);
  }
  function releaseStyle(node, name) {
    const properties = ownedStyles.get(node), value = properties?.get(name);
    if (!value) return;
    if (node.style.getPropertyValue(name) === value.after && node.style.getPropertyPriority(name) === 'important') {
      if (value.before) node.style.setProperty(name, value.before, value.priority); else node.style.removeProperty(name);
    }
    properties.delete(name); if (!properties.size) ownedStyles.delete(node);
  }
  function fieldHasError(node) {
    return Boolean(node.closest('._rsi-modal-fields-item-error,[aria-invalid="true"]') || node.matches('[aria-invalid="true"]') || node.querySelector('[aria-invalid="true"]'));
  }
  function warmFieldIcon(node) {
    const value = getComputedStyle(node).backgroundImage;
    const match = value.match(/^url\((["']?)(data:image\/svg\+xml[^]*?)\1\)$/i);
    if (!match) return;
    const comma = match[2].indexOf(','); if (comma < 0) return;
    let svg;
    try { svg = /;base64/i.test(match[2].slice(0, comma)) ? atob(match[2].slice(comma + 1)) : decodeURIComponent(match[2].slice(comma + 1)); } catch { return; }
    if (!/<svg\b/i.test(svg)) return;
    const branded = svg.replace(/<(svg|g|rect|path|circle|ellipse|polygon|polyline|line)\b[^>]*>/gi, tag => {
      const rectangle = /^<rect\b/i.test(tag);
      const width = Number(tag.match(/\bwidth=["']([\d.]+)["']/i)?.[1]);
      return tag.replace(/\b(fill|stroke)=(["'])(.*?)\2/gi, (attribute, property, quote, color) => {
        if (color.toLowerCase() === 'none' || color.toLowerCase() === 'transparent') return attribute;
        const target = rectangle && property.toLowerCase() === 'fill' ? width > 0 && width < 4 ? '#d8c69f' : '#f3ead8' : '#8c6425';
        return property + '=' + quote + target + quote;
      });
    });
    if (branded !== svg) ownStyle(node, 'background-image', 'url("data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(branded) + '")');
  }
  function decorateModal(open) {
    if (!open) return;
    const header = open.querySelector('._rsi-modal-header');
    ownStyle(header, 'width', 'calc(100% + 36px)');
    ownStyle(header, 'display', 'flex'); ownStyle(header, 'align-items', 'center');
    const title = open.querySelector('._rsi-modal-header-title');
    ownStyle(title, 'color', '#f0cd7a'); ownStyle(title, 'font-size', '16px');
    ownStyle(title, 'padding', '0'); ownStyle(title, 'margin', '0');
    ownStyle(title, 'flex', '1 1 auto'); ownStyle(title, 'text-align', 'left');
    ownStyle(title, 'white-space', 'normal');
    const close = open.querySelector('._rsi-modal-close-button');
    ownStyle(close, 'position', 'static'); ownStyle(close, 'margin', '0');
    ownStyle(close, 'flex', '0 0 44px'); ownStyle(close, 'width', '44px'); ownStyle(close, 'height', '44px');
    const colors = '._rsi-modal-fields-item > label,._rsi-modal-fields-title,._rsi-modal-shipping-rates-title,._rsi-modal-shipping-rates-item-title,._rsi-modal-checkout-line-title,._rsi-modal-checkout-line-value,._rsi-modal-checkout-line-price';
    open.querySelectorAll(colors).forEach(node => {
      if (fieldHasError(node)) releaseStyle(node, 'color'); else ownStyle(node, 'color', '#332719');
    });
    open.querySelectorAll('._rsi-modal-checkout-lines').forEach(lines => {
      ownStyle(lines, 'background', '#f3ead8'); ownStyle(lines, 'color', '#332719');
      ownStyle(lines, 'border', '1px solid #d8c69f'); ownStyle(lines, 'border-radius', '12px');
    });
    open.querySelectorAll('._rsi-modal-checkout-line[data-checkout-line="total"]').forEach(line => ownStyle(line, 'border-top', '1px solid #d8c69f'));
    open.querySelectorAll('._rsi-modal-fields-item-with-icon input:not([type="hidden"]),._rsi-modal-fields-item-with-icon select,._rsi-modal-fields-item-with-icon textarea').forEach(node => {
      if (fieldHasError(node)) releaseStyle(node, 'background-image'); else warmFieldIcon(node);
    });
    const submit = open.querySelector('button._rsi-modal-submit-button[type="submit"]');
    // These presentation properties also override the app's inline !important
    // source theme. Busy/disabled states, total markup and loading icons survive.
    ownStyle(submit, 'background', 'linear-gradient(135deg,#f0cd7a,#d4a84f)');
    ownStyle(submit, 'color', '#1b1409');
    ownStyle(submit, 'border', '1px solid #b5832e');
    ownStyle(submit, 'border-radius', '11px');
    ownStyle(submit, 'box-shadow', '0 5px 16px #9a681f25');
  }
  function transition(value) {
    state = value; root.dataset.ollaNativeState = value;
    const busy = ['initializing', 'opening', 'restoring'].includes(value);
    bridges.forEach(button => button.setAttribute('aria-busy', String(busy)));
    updateSticky();
  }
  function fail(reason) {
    if (disposed) return;
    clearTimeout(timer); pending = false; transition('reload-required');
    root.dataset.ollaNativeReason = reason;
    setStatus('No se pudo verificar el formulario. Recarga la página antes de volver a pedir.');
  }
  function nativeControl() {
    const nodes = [...new Set(owner.querySelectorAll(OWNER))].filter(visible);
    return nodes.length === 1 && !nodes[0].disabled && nodes[0].getAttribute('aria-disabled') !== 'true' ? nodes[0] : null;
  }
  function nativeCopy() {
    const button = nativeControl();
    if (!button) return null;
    const copies = [...button.children].filter(node => node.tagName === 'SPAN' && visible(node) && node.closest(OWNER) === button);
    return copies.length === 1 ? copies[0] : null;
  }
  async function readCart() {
    const controller = new AbortController(); reads.add(controller);
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const route = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const response = await fetch(route + 'cart.js', {method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal});
      if (!response.ok) throw Error('Public cart read failed');
      return cartFingerprint(await response.json());
    } finally { clearTimeout(timeout); reads.delete(controller); }
  }
  function later(delay = 200) { clearTimeout(timer); timer = setTimeout(tick, delay); }
  function beginOpening() {
    transition('opening'); pending = false; duringOpen = null; intentAt = 0; closedAt = 0; clear = null; restoreAdd = null;
    deadline = now() + 45000; setStatus(''); later();
  }
  function requestOpen() {
    if (disposed || state === 'reload-required') return;
    if (modal()) return;
    pending = true;
    if (state !== 'ready') { setStatus(state === 'restoring' ? 'Estamos preparando el formulario para abrirlo de nuevo…' : 'El formulario está cargando…'); return; }
    const copy = nativeCopy();
    if (!copy) { pending = false; setStatus('El formulario todavía está cargando. Espera un momento e inténtalo de nuevo.'); return; }
    beginOpening(); dispatching = true;
    try { copy.click(); } finally { dispatching = false; }
    schedule();
  }
  async function checkRestoration() {
    if (readRunning || state !== 'restoring' || !restorationEvidence({baseline, duringOpen, intentAt, closedAt, clear, restoreAdd})) return;
    const generation = epoch; readRunning = true;
    try {
      const current = await readCart();
      if (disposed || generation !== epoch || state !== 'restoring') return;
      if (current === baseline) { transition('ready'); setStatus(''); clearTimeout(timer); if (pending) requestOpen(); }
    } catch { if (!disposed && generation === epoch && state === 'restoring') root.dataset.ollaNativeReason = 'restore-read-error'; }
    finally { readRunning = false; }
  }
  function watchModal() {
    frame = 0;
    if (disposed) return;
    const activeModal = modal();
    const open = Boolean(activeModal);
    updateSticky(activeModal);
    decorateModal(activeModal);
    if (open && !modalWasOpen) {
      if (state !== 'opening') { fail('unverified-opening'); modalWasOpen = true; return; }
      transition('open'); pending = false; clearTimeout(timer);
      const generation = epoch;
      readCart().then(fingerprint => { if (!disposed && generation === epoch && state === 'open' && !intentAt && fingerprint) duringOpen = {fingerprint, completedAt: now()}; }).catch(() => {});
    }
    if (!open && modalWasOpen) {
      if (!intentAt) { fail('unattributed-close'); modalWasOpen = false; return; }
      closedAt = now(); transition('restoring'); deadline = now() + 30000;
      setStatus('Estamos preparando el formulario para abrirlo de nuevo…'); checkRestoration(); later();
    }
    modalWasOpen = open;
  }
  function schedule() { if (!frame) frame = requestAnimationFrame(watchModal); }
  function tick() {
    if (disposed) return;
    watchModal();
    if (state === 'opening' && now() > deadline) { fail('opening-timeout'); return; }
    if (state === 'restoring') {
      if (now() > deadline) { fail('restoration-timeout'); return; }
      checkRestoration();
    }
    if (state === 'opening' || state === 'restoring') later(350);
  }
  function recordIntent() {
    if (!modal() || intentAt) return;
    intentAt = now(); epoch += 1;
    // Escape can leave a native control popup open without closing the modal.
    // Discard that non-close intent in the next task after the app's handler.
    const generation = epoch;
    setTimeout(() => { if (!disposed && generation === epoch && state === 'open' && modal()) intentAt = 0; }, 0);
  }
  function belongsToModal(target) { const open = modal(); return Boolean(open && target?.closest && open.contains(target)); }
  function nativeOrderReady(open) {
    if (state !== 'open' || !open || root.dataset.nativeReady !== 'true') return false;
    const variant = open.querySelector('input[name="item_variant_id[0]"]');
    const quantity = open.querySelector('input[name="item_quantity[0]"]');
    const total = open.querySelector('[data-checkout-line="total"][data-order-total]');
    return variant?.value === root.dataset.variantId && Number(quantity?.value) === 1 && Number(total?.dataset.orderTotal) === Number(root.dataset.expectedTotal);
  }
  function guardSubmit(event) {
    const open = modal();
    if (!open || !belongsToModal(event.target)) return;
    const submit = event.type === 'submit' || event.target.closest?.('button[type="submit"],input[type="submit"]');
    if (!submit) return;
    if (nativeOrderReady(open)) { open.querySelector('[data-olla-native-error]')?.remove(); return; }
    event.preventDefault(); event.stopImmediatePropagation();
    setStatus('El resumen del pedido todavía se está verificando. Espera un momento y revisa el total.');
    let error = open.querySelector('[data-olla-native-error]');
    if (!error) {
      error = document.createElement('p'); error.dataset.ollaNativeError = ''; error.setAttribute('role', 'alert');
      error.style.cssText = 'padding:10px;margin:10px 0;color:#7f2718;background:#fff2dc;border:1px solid #caa56b;border-radius:8px;font:400 13px/1.5 Poppins,sans-serif';
      open.querySelector('button[type="submit"]')?.insertAdjacentElement('beforebegin', error);
    }
    error.textContent = 'El resumen del pedido todavía se está verificando. Espera un momento y revisa el total.';
  }
  window.addEventListener('click', event => {
    guardSubmit(event);
    if (event.defaultPrevented) return;
    const target = event.target;
    const bridge = target.closest?.('[data-olla-scroll-native]');
    if (bridge && root.contains(bridge)) { event.preventDefault(); event.stopImmediatePropagation(); requestOpen(); return; }
    const button = nativeControl();
    if (button && button.contains(target)) {
      if (dispatching && state === 'opening') return;
      if (state !== 'ready') { event.preventDefault(); event.stopImmediatePropagation(); requestOpen(); return; }
      beginOpening(); schedule(); return;
    }
    const open = modal();
    if (open && target === open) {
      const close = open.querySelector('#_rsi-cod-form-modal-close');
      if (visible(close) && !close.disabled && close.getAttribute('aria-disabled') !== 'true') {
        event.preventDefault(); event.stopImmediatePropagation();
        recordIntent(); close.click();
      }
      return;
    }
    if (open && target.closest?.('#_rsi-cod-form-modal-close,._rsi-modal-close-button')) recordIntent();
  }, {capture: true, signal: abort.signal});
  window.addEventListener('submit', guardSubmit, {capture: true, signal: abort.signal});
  window.addEventListener('keydown', event => {
    const bridge = event.target.closest?.('[data-olla-scroll-native]');
    if (event.key === ' ' && bridge && root.contains(bridge)) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (!event.repeat) requestOpen();
      return;
    }
    if (event.key === 'Escape' && !event.repeat && !event.isComposing) {
      const open = modal();
      // Leave Escape to an active native select menu. Otherwise activate the
      // genuine close control so keyboard users get the same native cleanup.
      if (!open || event.target.closest?.('select')) return;
      const close = open.querySelector('#_rsi-cod-form-modal-close');
      if (!visible(close) || close.disabled || close.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault(); event.stopImmediatePropagation();
      recordIntent(); close.click();
    }
  }, {capture: true, signal: abort.signal});
  const mutation = new MutationObserver(schedule);
  mutation.observe(document.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['class']});
  window.addEventListener('scroll', schedule, {passive: true, signal: abort.signal});
  window.addEventListener('resize', schedule, {passive: true, signal: abort.signal});
  let viewport;
  if (typeof IntersectionObserver === 'function') {
    viewport = new IntersectionObserver(schedule, {threshold: [0, 1]});
    viewport.observe(owner); if (footer) viewport.observe(footer);
  }
  let resource;
  try {
    resource = new PerformanceObserver(list => {
      if (!intentAt || !['open', 'restoring'].includes(state)) return;
      for (const entry of list.getEntries()) {
        let url; try { url = new URL(entry.name, location.href); } catch { continue; }
        if (url.origin !== location.origin || entry.startTime < intentAt) continue;
        if (/\/cart\/clear(?:\.js)?$/.test(url.pathname)) {
          if (Number(entry.responseStatus) >= 400) { fail('native-clear-failed'); return; }
          if (!clear || entry.startTime > clear.startTime) { clear = entry; restoreAdd = null; }
        } else if (clear && /\/cart\/add(?:\.js)?$/.test(url.pathname) && entry.startTime >= clear.responseEnd) restoreAdd = entry;
      }
      checkRestoration();
    });
    try { resource.observe({type: 'resource', buffered: true}); } catch { resource.observe({entryTypes: ['resource']}); }
  } catch { fail('resource-observer-unavailable'); }
  function cleanup() {
    disposed = true; abort.abort(); clearTimeout(timer); cancelAnimationFrame(frame); mutation.disconnect(); resource?.disconnect(); viewport?.disconnect();
    if (sticky) sticky.hidden = true;
    reads.forEach(controller => controller.abort()); instances.delete(root);
    ownedStyles.forEach((properties, node) => properties.forEach((value, name) => {
      if (node.style.getPropertyValue(name) !== value.after || node.style.getPropertyPriority(name) !== 'important') return;
      if (value.before) node.style.setProperty(name, value.before, value.priority); else node.style.removeProperty(name);
    }));
    document.querySelectorAll('[data-olla-native-error]').forEach(node => node.remove());
    delete root.dataset.ollaNativeState; delete root.dataset.ollaNativeReason;
    bridges.forEach(button => button.removeAttribute('aria-busy'));
    bridgeAccessibility.forEach(({node, role, popup}) => {
      if (node.getAttribute('role') === 'button') { if (role == null) node.removeAttribute('role'); else node.setAttribute('role', role); }
      if (node.getAttribute('aria-haspopup') === 'dialog') { if (popup == null) node.removeAttribute('aria-haspopup'); else node.setAttribute('aria-haspopup', popup); }
    });
  }
  instances.set(root, cleanup);
  document.addEventListener('shopify:section:unload', event => { if (event.target.contains(root)) cleanup(); }, {signal: abort.signal});
  if (state !== 'reload-required') {
    transition('initializing');
    readCart().then(fingerprint => { if (disposed || state === 'reload-required') return; baseline = fingerprint; transition('ready'); setStatus(''); if (pending) requestOpen(); }).catch(() => fail('initial-cart-read-failed'));
  }
}

if (typeof document !== 'undefined') {
  document.querySelectorAll(ROOT).forEach(mount);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll(ROOT).forEach(mount));
}
